"use client";

import { useEffect, useRef } from "react";
import { gsap } from "gsap";

interface AnimatedJLogoProps {
  width?: number;
  height?: number;
  className?: string;
  strokeWidth?: number;
  autoPlay?: boolean;
  duration?: number;
  delay?: number;
}

export default function AnimatedJLogo({
  width = 242,
  height = 118,
  className = '',
  strokeWidth = 8,
  autoPlay = true,
  duration = 1.0,
  delay = 0.25,
}: AnimatedJLogoProps) {
  const pathRef = useRef<SVGPathElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const path = pathRef.current;
    if (!path) return;

    // Get the total length of the path
    const pathLength = path.getTotalLength();

    // Set up the stroke-dasharray and stroke-dashoffset
    path.style.strokeDasharray = `${pathLength}`;
    path.style.strokeDashoffset = `${pathLength}`;

    if (autoPlay) {
      // Create the drawing animation
      gsap.fromTo(
        path,
        {
          strokeDashoffset: pathLength,
        },
        {
          strokeDashoffset: 0,
          duration: duration,
          delay: delay,
        },
      );
    }

    // Hover animation function
    const animateDraw = () => {
      gsap.fromTo(
        path,
        {
          strokeDashoffset: pathLength,
        },
        {
          strokeDashoffset: 0,
          duration: duration,
          delay: delay,
        },
      );
    };

    // Add hover listeners
    const svg = svgRef.current;
    if (svg && !autoPlay) {
      svg.addEventListener('mouseenter', animateDraw);
      return () => {
        svg.removeEventListener('mouseenter', animateDraw);
      };
    }
  }, [autoPlay, duration, delay]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      viewBox="0 0 241 138"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Animated J Logo"
      role="img"
    >
      <path
        ref={pathRef}
        d="M92.268 32.2912C92.268 32.2912 101.971 26.8568 107.458 24.7587C122.884 18.8607 135.042 17.8071 147.888 16.6081C160.733 15.4091 172.654 15.6956 187.59 17.0118C196.613 17.8069 212.821 19.6515 212.821 19.6515C212.821 19.6515 198.75 37.1432 190.983 46.4412C179.492 60.1965 173.704 68.5166 161.221 81.3783C149.888 93.0564 144.557 101.34 130.49 109.522C118.168 116.689 104.608 119.255 96.889 120.634C74.0559 124.711 45.7331 120.719 31.3797 117.236C23.2021 115.251 9.04941 108.049 10.798 99.8173C12.3524 92.4999 34.7777 87.1994 47.0098 85.9762C68.6831 83.8089 103.701 81.4026 140.789 81.3783C163.291 81.3636 181.394 81.3783 199.366 81.3783C226.872 81.3783 230.63 82.6591 230.63 82.6591"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}
